/**
 * Theme preference: device-local storage and document application.
 *
 * The theme is a display preference for one device, so it is never written to the
 * account document. In the old app it lived in synced state, which meant one
 * person switching to dark flipped it for everyone else signed in, and burned a
 * write plus a version bump on the shared document that could lose a real edit.
 *
 * Audit references prevented here:
 *   theme.js #3   (theme stored in the shared account document)
 *   theme.js #10  (theme validation duplicated, bad values silently defaulting)
 *   theme.js #12  (no OS colour-scheme support, no theme-color meta)
 */

import { COLOR_TOKENS } from '../../../shared/design/tokens.js';
import { reportClientError } from '../errors/report-client-error.js';

/** localStorage key. Namespaced so it cannot collide with anything else. */
export const THEME_STORAGE_KEY = 'eaxfuel.theme';

/**
 * What a user can choose. `system` follows the operating system and is the
 * default on first run, so the app opens in dark mode for someone whose device
 * is in dark mode.
 */
export const THEME_PREFERENCES = Object.freeze(['system', 'light', 'dark']);

/** Preference used when nothing is stored, or when what is stored is invalid. */
export const DEFAULT_THEME_PREFERENCE = 'system';

/** The media query that reports the operating system's colour scheme. */
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * Whether a value is a theme preference this build understands.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isThemePreference(value) {
  return typeof value === 'string' && THEME_PREFERENCES.includes(value);
}

/**
 * Read the stored preference.
 *
 * An unreadable store (Safari private mode throws on access) or an unrecognised
 * stored value both fall back to the default, and both are reported rather than
 * swallowed.
 *
 * @param {Storage} [storage]
 * @returns {'system'|'light'|'dark'}
 */
export function readThemePreference(storage = globalThis.localStorage) {
  let stored = null;
  try {
    stored = storage?.getItem(THEME_STORAGE_KEY) ?? null;
  } catch (error) {
    reportClientError(error, { scope: 'theme.preference', operation: 'read' }, 'warning');
    return DEFAULT_THEME_PREFERENCE;
  }

  if (stored === null) return DEFAULT_THEME_PREFERENCE;
  if (isThemePreference(stored)) return stored;

  reportClientError(
    new Error(`Unrecognised stored theme preference: ${JSON.stringify(stored)}`),
    { scope: 'theme.preference', operation: 'validate' },
    'warning',
  );
  return DEFAULT_THEME_PREFERENCE;
}

/**
 * Persist the preference for this device.
 *
 * @param {'system'|'light'|'dark'} preference
 * @param {Storage} [storage]
 * @returns {boolean} Whether the write landed. A caller that needs to tell the
 *   user "your choice will not stick on this device" can act on false.
 * @throws {TypeError} if the preference is not one this build understands.
 */
export function writeThemePreference(preference, storage = globalThis.localStorage) {
  if (!isThemePreference(preference)) {
    throw new TypeError(`Not a theme preference: ${JSON.stringify(preference)}`);
  }
  try {
    storage?.setItem(THEME_STORAGE_KEY, preference);
    return true;
  } catch (error) {
    reportClientError(error, { scope: 'theme.preference', operation: 'write' }, 'warning');
    return false;
  }
}

/**
 * Resolve a preference to an actual palette name.
 *
 * @param {'system'|'light'|'dark'} preference
 * @param {boolean} systemPrefersDark
 * @returns {'light'|'dark'}
 */
export function resolveTheme(preference, systemPrefersDark) {
  if (preference === 'light' || preference === 'dark') return preference;
  return systemPrefersDark ? 'dark' : 'light';
}

/**
 * Apply a resolved theme to the document.
 *
 * Two side effects, both on the DOM and neither in a render body: the
 * `data-theme` attribute that the token stylesheet keys off, and the
 * `theme-color` meta that colours mobile browser chrome.
 *
 * @param {'light'|'dark'} resolvedTheme
 * @param {Document} [doc]
 * @returns {void}
 */
export function applyThemeToDocument(resolvedTheme, doc = globalThis.document) {
  if (!doc?.documentElement) return;
  doc.documentElement.dataset.theme = resolvedTheme;

  const meta = doc.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', COLOR_TOKENS[resolvedTheme].bgPage);
}
